import { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface BondsPanelProps {
  sessionId: string;
  socket: Socket | null;
  isConnected: boolean;
  numPlayers: number;
}

interface BondVoteData {
  id: string;
  fromTeam: TeamColor;
  options: { id: string; text: string }[];
  winningOptionId: string | null;
  winningText: string | null;
  isActive: boolean;
}

interface CharacterData {
  id: string;
  name: string;
  team: TeamColor;
  bonds?: string[];
}

type BondState = 'idle' | 'voting' | 'results' | 'winner';


const TEAM_NAMES: Record<TeamColor, string> = {
  BLUE: 'Blu',
  RED: 'Rossa',
  GREEN: 'Verde',
  YELLOW: 'Gialla',
};

const TEAM_COLORS: Record<number, TeamColor[]> = {
  2: ['RED', 'BLUE'],
  3: ['RED', 'GREEN', 'BLUE'],
  4: ['RED', 'GREEN', 'BLUE', 'YELLOW'],
};

const TEAM_COLOR_CLASSES: Record<TeamColor, { bg: string; bgActive: string; text: string; ring: string; border: string }> = {
  BLUE: { bg: 'bg-blue-600/30', bgActive: 'bg-blue-600', text: 'text-blue-300', ring: 'ring-blue-400', border: 'border-blue-500' },
  RED: { bg: 'bg-red-600/30', bgActive: 'bg-red-600', text: 'text-red-300', ring: 'ring-red-400', border: 'border-red-500' },
  GREEN: { bg: 'bg-green-600/30', bgActive: 'bg-green-600', text: 'text-green-300', ring: 'ring-green-400', border: 'border-green-500' },
  YELLOW: { bg: 'bg-yellow-600/30', bgActive: 'bg-yellow-600', text: 'text-yellow-300', ring: 'ring-yellow-400', border: 'border-yellow-500' },
};

const DEFAULT_OPTIONS = [
  'è il migliore amico di [ROSSO]',
  'non sopporta [VERDE]',
  '',
  '',
];

export function BondsPanel({ sessionId, socket, isConnected, numPlayers }: BondsPanelProps) {
  const [selectedTeam, setSelectedTeam] = useState<TeamColor>('RED');
  const [options, setOptions] = useState<Record<TeamColor, string[]>>({
    BLUE: [...DEFAULT_OPTIONS],
    RED: [...DEFAULT_OPTIONS],
    GREEN: [...DEFAULT_OPTIONS],
    YELLOW: [...DEFAULT_OPTIONS],
  });
  const [bondVotes, setBondVotes] = useState<Record<TeamColor, BondVoteData | null>>({
    BLUE: null,
    RED: null,
    GREEN: null,
    YELLOW: null,
  });
  const [bondState, setBondState] = useState<Record<TeamColor, BondState>>({
    BLUE: 'idle',
    RED: 'idle',
    GREEN: 'idle',
    YELLOW: 'idle',
  });
  const [characters, setCharacters] = useState<CharacterData[]>([]);
  const [voteResults, setVoteResults] = useState<Record<string, number>>({});
  const [autoAssigning, setAutoAssigning] = useState(false);

  const availableTeams = TEAM_COLORS[numPlayers] || TEAM_COLORS[2];

  // Fetch characters and existing bond votes on mount
  useEffect(() => {
    fetchCharacters();
    fetchBondVotes();
  }, [sessionId]);

  // Listen for bond vote events
  useEffect(() => {
    if (!socket) return;

    const handleClosed = (data: { voteId: string }) => {
      // Find which team this vote belongs to
      for (const [team, vote] of Object.entries(bondVotes)) {
        if (vote?.id === data.voteId) {
          setBondState(prev => ({ ...prev, [team]: 'results' }));
          fetchBondVotes(); // Refresh to get winner
          break;
        }
      }
    };

    const handleVoteSubmitted = (data: { voteId: string }) => {
      // Refresh vote results when a vote is submitted
      const currentVote = bondVotes[selectedTeam];
      if (currentVote?.id === data.voteId) {
        fetchVoteResults(data.voteId);
      }
    };

    socket.on('bond-vote:closed', handleClosed);
    socket.on('bond-vote:vote-submitted', handleVoteSubmitted);

    return () => {
      socket.off('bond-vote:closed', handleClosed);
      socket.off('bond-vote:vote-submitted', handleVoteSubmitted);
    };
  }, [socket, bondVotes, selectedTeam]);

  // Fetch vote results when voting starts
  useEffect(() => {
    const currentVote = bondVotes[selectedTeam];
    if (currentVote && bondState[selectedTeam] === 'voting') {
      fetchVoteResults(currentVote.id);
    }
  }, [bondVotes, selectedTeam, bondState]);

  const fetchCharacters = async () => {
    try {
      // Get winning sheets from sheet votes
      const response = await fetch(`/api/sheet-votes/session/${sessionId}/latest`);
      if (response.ok) {
        const data = await response.json();
        const votes = data.votes || [];

        // Map winning sheets to characters format
        const chars: CharacterData[] = votes
          .filter((v: any) => v.winnerSheet)
          .map((v: any) => ({
            id: v.winnerSheet.id,
            name: v.winnerSheet.name,
            team: v.teamColor as TeamColor,
            bonds: v.winnerSheet.bonds || [],
          }));

        setCharacters(chars);

        // Update options with character's defined bonds
        const newOptions: Record<TeamColor, string[]> = {
          BLUE: [...DEFAULT_OPTIONS],
          RED: [...DEFAULT_OPTIONS],
          GREEN: [...DEFAULT_OPTIONS],
          YELLOW: [...DEFAULT_OPTIONS],
        };

        for (const char of chars) {
          if (char.bonds && char.bonds.length > 0) {
            // Use character's bonds, pad to 4 if needed
            const charBonds = [...char.bonds];
            while (charBonds.length < 4) {
              charBonds.push('');
            }
            newOptions[char.team] = charBonds.slice(0, 4);
          }
        }

        setOptions(newOptions);
      }
    } catch (error) {
      console.error('[BondsPanel] Error fetching characters:', error);
    }
  };

  const fetchBondVotes = async () => {
    try {
      const response = await fetch(`/api/bond-votes/session/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        const votes = data.bondVotes || [];

        // Map votes by team
        const votesByTeam: Record<TeamColor, BondVoteData | null> = {
          BLUE: null,
          RED: null,
          GREEN: null,
          YELLOW: null,
        };
        const stateByTeam: Record<TeamColor, BondState> = {
          BLUE: 'idle',
          RED: 'idle',
          GREEN: 'idle',
          YELLOW: 'idle',
        };

        for (const vote of votes) {
          const team = vote.fromTeam as TeamColor;
          votesByTeam[team] = vote;

          if (vote.isActive) {
            stateByTeam[team] = 'voting';
          } else if (vote.winningText) {
            stateByTeam[team] = 'winner';
          } else {
            stateByTeam[team] = 'results';
          }
        }

        setBondVotes(votesByTeam);
        setBondState(stateByTeam);
      }
    } catch (error) {
      console.error('[BondsPanel] Error fetching bond votes:', error);
    }
  };

  const fetchVoteResults = async (voteId: string) => {
    try {
      const response = await fetch(`/api/bond-votes/${voteId}/results`);
      if (response.ok) {
        const data = await response.json();
        const counts: Record<string, number> = {};
        for (const opt of data.options || []) {
          counts[opt.id] = opt.votes || 0;
        }
        setVoteResults(counts);
      }
    } catch (error) {
      console.error('[BondsPanel] Error fetching vote results:', error);
    }
  };

  const handleOptionChange = (index: number, value: string) => {
    setOptions(prev => ({
      ...prev,
      [selectedTeam]: prev[selectedTeam].map((opt, i) => i === index ? value : opt),
    }));
  };

  const handleStartVoting = async () => {
    const teamOptions = options[selectedTeam].filter(opt => opt.trim().length > 0);

    if (teamOptions.length < 2) {
      toast.error('Inserisci almeno 2 opzioni');
      return;
    }

    if (!isConnected) {
      toast.error('Socket non connesso');
      return;
    }

    try {
      // Create bond vote
      const createResponse = await fetch('/api/bond-votes/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          fromTeam: selectedTeam,
          options: teamOptions,
        }),
      });

      if (!createResponse.ok) {
        const error = await createResponse.json();
        throw new Error(error.error || 'Errore creazione votazione');
      }

      const { bondVote } = await createResponse.json();

      // Start the vote
      const startResponse = await fetch(`/api/bond-votes/${bondVote.id}/start`, {
        method: 'POST',
      });

      if (!startResponse.ok) {
        const error = await startResponse.json();
        throw new Error(error.error || 'Errore avvio votazione');
      }

      setBondState(prev => ({ ...prev, [selectedTeam]: 'voting' }));
      await fetchBondVotes();
      toast.success(`Votazione legame Squadra ${TEAM_NAMES[selectedTeam]} avviata!`);

    } catch (error: any) {
      console.error('[BondsPanel] Error starting vote:', error);
      toast.error(error.message || 'Errore avvio votazione');
    }
  };

  const handleCloseVoting = async () => {
    const vote = bondVotes[selectedTeam];
    if (!vote) return;

    try {
      await fetch(`/api/bond-votes/${vote.id}/close`, {
        method: 'POST',
      });

      setBondState(prev => ({ ...prev, [selectedTeam]: 'results' }));
      await fetchBondVotes();
      toast.success('Votazione chiusa');
    } catch (error) {
      console.error('[BondsPanel] Error closing vote:', error);
      toast.error('Errore chiusura votazione');
    }
  };

  const handleShowWinner = async () => {
    const vote = bondVotes[selectedTeam];
    if (!vote) return;

    try {
      await fetch(`/api/bond-votes/${vote.id}/show-winner`, {
        method: 'POST',
      });

      setBondState(prev => ({ ...prev, [selectedTeam]: 'winner' }));
      toast.success('Vincitore mostrato');
    } catch (error) {
      console.error('[BondsPanel] Error showing winner:', error);
      toast.error('Errore visualizzazione vincitore');
    }
  };

  const handleReset = async () => {
    const vote = bondVotes[selectedTeam];

    if (vote) {
      try {
        await fetch(`/api/bond-votes/${vote.id}`, {
          method: 'DELETE',
        });
      } catch (error) {
        console.error('[BondsPanel] Error deleting vote:', error);
      }
    }

    setBondState(prev => ({ ...prev, [selectedTeam]: 'idle' }));
    setBondVotes(prev => ({ ...prev, [selectedTeam]: null }));

    // Reset to standby
    if (socket && isConnected) {
      socket.emit('admin:set-projection', {
        sessionId,
        mode: { type: 'standby' },
      });
    }

    await fetchBondVotes();
    toast.success('Votazione resettata');
  };

  const handleShowAllWinners = async () => {
    if (!socket || !isConnected) return;

    socket.emit('admin:set-projection', {
      sessionId,
      mode: { type: 'bond-vote-all-winners', sessionId },
    });

    toast.success('Tutti i legami mostrati');
  };

  const handleAutoAssign = async () => {
    if (autoAssigning || !isConnected) return;

    setAutoAssigning(true);
    try {
      const response = await fetch('/api/bond-votes/auto-assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Errore auto-assegnazione');
      }

      toast.success('Legami auto-assegnati!');

      // Refresh bond votes after a delay (let animation play)
      setTimeout(() => {
        fetchBondVotes();
        setAutoAssigning(false);
      }, 5500);
    } catch (error: any) {
      console.error('[BondsPanel] Error auto-assigning bonds:', error);
      toast.error(error.message || 'Errore auto-assegnazione legami');
      setAutoAssigning(false);
    }
  };

  const handleAddVote = async (optionId: string) => {
    const vote = bondVotes[selectedTeam];
    if (!vote) return;

    try {
      const response = await fetch(`/api/bond-votes/${vote.id}/admin-votes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optionId, count: 1 }),
      });

      if (response.ok) {
        await fetchVoteResults(vote.id);
        toast.success('Voto aggiunto!');
      }
    } catch (error) {
      console.error('[BondsPanel] Error adding vote:', error);
      toast.error('Errore aggiunta voto');
    }
  };

  const handleRemoveVote = async (optionId: string) => {
    const vote = bondVotes[selectedTeam];
    if (!vote) return;

    try {
      const response = await fetch(`/api/bond-votes/${vote.id}/remove-vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optionId }),
      });

      if (response.ok) {
        await fetchVoteResults(vote.id);
        toast.success('Voto rimosso!');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Errore rimozione voto');
      }
    } catch (error) {
      console.error('[BondsPanel] Error removing vote:', error);
      toast.error('Errore rimozione voto');
    }
  };

  // Get hero name for a team
  const getHeroName = (team: TeamColor): string => {
    const character = characters.find(c => c.team === team);
    return character?.name || `Eroe ${TEAM_NAMES[team]}`;
  };

  const currentState = bondState[selectedTeam];
  const currentVote = bondVotes[selectedTeam];
  const validOptionsCount = options[selectedTeam].filter(opt => opt.trim().length > 0).length;

  // Count completed bonds
  const completedBonds = availableTeams.filter(team => bondVotes[team]?.winningText).length;

  return (
    <div className="space-y-6">
      {/* Auto Assign Button */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <button
          onClick={handleAutoAssign}
          disabled={autoAssigning || !isConnected}
          className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-6 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-3"
        >
          {autoAssigning ? (
            <>
              <span className="animate-spin">🎲</span>
              <span>Assegnazione in corso...</span>
            </>
          ) : (
            <>
              <span>🔗</span>
              <span>Auto assegna Legami</span>
            </>
          )}
        </button>

        {/* Status Overview */}
        <div className="space-y-2 mt-4">
          {availableTeams.map((team) => {
            const classes = TEAM_COLOR_CLASSES[team];
            const vote = bondVotes[team];
            const state = bondState[team];

            return (
              <div
                key={team}
                className={`rounded-lg px-4 py-3 ${classes.bg} border ${classes.border}`}
              >
                {vote?.winningText ? (
                  <p className="text-gray-200 text-sm">
                    <span className={`font-bold ${classes.text}`}>{getHeroName(team)}</span>{' '}
                    {vote.winningText}
                  </p>
                ) : state === 'voting' ? (
                  <p className="text-sm">
                    <span className={`font-bold ${classes.text}`}>{TEAM_NAMES[team]}</span>{' '}
                    <span className="text-yellow-300">In votazione...</span>
                  </p>
                ) : (
                  <p className="text-sm">
                    <span className={`font-bold ${classes.text}`}>{TEAM_NAMES[team]}</span>{' '}
                    <span className="text-gray-500">In attesa</span>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Team Selection */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-gray-400 text-sm font-semibold whitespace-nowrap">Squadra:</span>
          {availableTeams.map((team) => {
            const classes = TEAM_COLOR_CLASSES[team];
            const isSelected = selectedTeam === team;
            const state = bondState[team];
            const vote = bondVotes[team];

            return (
              <button
                key={team}
                onClick={() => setSelectedTeam(team)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  isSelected
                    ? `${classes.bgActive} text-white ring-2 ${classes.ring}`
                    : `${classes.bg} ${classes.text} hover:opacity-80`
                }`}
              >
                <span>{TEAM_NAMES[team]}</span>
                {state === 'voting' && (
                  <span className="bg-yellow-500 text-black text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
                    LIVE
                  </span>
                )}
                {vote?.winningText && state !== 'voting' && (
                  <span className="text-green-400 text-sm">✓</span>
                )}
              </button>
            );
          })}

          {/* Show All Winners button */}
          {completedBonds >= 2 && (
            <button
              onClick={handleShowAllWinners}
              className="ml-auto px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-all"
            >
              Mostra Tutti i Legami ({completedBonds})
            </button>
          )}
        </div>
      </div>

      {/* Bond Configuration Panel */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4">
          Legame: {getHeroName(selectedTeam)}
        </h3>

        {currentState === 'idle' && (
          <>
            {/* Options */}
            <div className="space-y-3 mb-6">
              <label className="text-gray-400 text-sm font-semibold">
                Opzioni legame (usa [BLU], [ROSSO], [VERDE], [GIALLO] per i nomi degli eroi)
              </label>
              {options[selectedTeam].map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm w-6">{index + 1}.</span>
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Opzione ${index + 1} - es. "è innamorato di [ROSSO]"`}
                    className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              ))}
              <p className="text-gray-500 text-xs">
                Opzioni compilate: {validOptionsCount}/4
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleStartVoting}
                disabled={validOptionsCount < 2 || !isConnected}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-bold transition-all"
              >
                Avvia Votazione
              </button>
              <button
                onClick={handleReset}
                className="bg-gray-600 hover:bg-gray-700 text-white py-3 px-4 rounded-lg font-semibold transition-all"
              >
                Reset
              </button>
            </div>
          </>
        )}

        {currentState === 'voting' && (
          <div className="py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl animate-pulse">🗳️</span>
                <div>
                  <p className="text-lg text-white font-bold">Votazione in corso</p>
                  <p className="text-gray-400 text-sm">Squadra {TEAM_NAMES[selectedTeam]} sta votando</p>
                </div>
              </div>
              <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded-full font-semibold animate-pulse">
                LIVE
              </span>
            </div>

            {/* Opzioni con bottoni +/- */}
            <div className="space-y-2 mb-6">
              {currentVote?.options.map((opt) => {
                const count = voteResults[opt.id] || 0;
                const totalVotes = Object.values(voteResults).reduce((a, b) => a + b, 0);
                const percentage = totalVotes > 0 ? (count / totalVotes) * 100 : 0;

                return (
                  <div key={opt.id} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-300">{opt.text}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">
                          {count} ({percentage.toFixed(0)}%)
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleRemoveVote(opt.id)}
                            disabled={count === 0}
                            className="bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-2 py-0.5 rounded text-xs font-bold transition-all"
                          >
                            −
                          </button>
                          <button
                            onClick={() => handleAddVote(opt.id)}
                            className="bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded text-xs font-bold transition-all"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-purple-500 transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottone chiudi */}
            <div className="flex justify-center">
              <button
                onClick={handleCloseVoting}
                className="bg-red-600 hover:bg-red-700 text-white py-2 px-6 rounded-lg font-semibold transition-all"
              >
                Chiudi Votazione
              </button>
            </div>
          </div>
        )}

        {currentState === 'results' && (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">📊</div>
            <p className="text-2xl text-white font-bold mb-4">Risultati</p>
            <p className="text-gray-400 mb-6">
              I risultati sono mostrati su Display e Audience
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleShowWinner}
                className="bg-yellow-600 hover:bg-yellow-700 text-white py-3 px-6 rounded-lg font-bold transition-all"
              >
                Mostra Vincitore
              </button>
              <button
                onClick={handleReset}
                className="bg-gray-600 hover:bg-gray-700 text-white py-3 px-6 rounded-lg font-semibold transition-all"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {currentState === 'winner' && (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🏆</div>
            <p className="text-2xl text-white font-bold mb-2">Legame Scelto</p>
            {currentVote?.winningText && (
              <div className="bg-green-900/30 border border-green-500 rounded-lg p-4 mb-6 inline-block">
                <p className="text-green-300 text-lg font-semibold">
                  {getHeroName(selectedTeam)} {currentVote.winningText}
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleReset}
                className="bg-purple-600 hover:bg-purple-700 text-white py-3 px-8 rounded-lg font-bold transition-all"
              >
                Nuovo Legame
              </button>
            </div>
          </div>
        )}

        {!isConnected && currentState === 'idle' && (
          <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
            <p className="text-yellow-300 text-sm">
              Socket non connesso. La votazione non può essere avviata.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
